import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./MyAssetsCards.module.css";

const getUserHistoryUrl = "https://usdi-api-256611876551.europe-west1.run.app/cash_redeem/get_user_history";

function formatAmount(val) {
  const n = parseFloat(val);
  return Number.isFinite(n) ? n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—";
}

function parseDate(timestampStr) {
  if (!timestampStr) return null;
  const t = timestampStr.replace(",", "");
  const [datePart, timePart] = t.split(" ");
  if (!datePart) return null;
  const [month, day, year] = datePart.split("/");
  if (!year) return null;
  const iso = `${year}-${month}-${day}${timePart ? `T${timePart}Z` : ""}`;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Deposit / Withdraw history from get_user_history (same API as TransactionHistory).
 * TYPE: Mint -> Deposit (green), Redeem -> Withdraw (red)
 * AMOUNT: +X USDC for mint, -X USDi for redeem
 * DATE, TX (link to explorer)
 */
function DepositWithdrawCard({ walletAddress, maxRows = 5 }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!walletAddress) {
      setRows([]);
      setLoading(false);
      return;
    }
    const fetchData = async () => {
      try {
        const res = await fetch(
          `${getUserHistoryUrl}?user_address=${walletAddress}&page=1&page_size=${maxRows}`
        );
        const data = await res.json();
        const txHist = data?.transaction_history || [];
        const list = txHist.map((tx) => {
          const createdAt = parseDate(tx.txs_created_timestamp);
          const dateStr = createdAt
            ? createdAt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
            : "—";
          const isMint = (tx.txs_type || "").toLowerCase() === "mint";
          const amount = formatAmount(tx.from_token_amount || 0);
          const currency = isMint ? "USDC" : "USDi";
          const signedAmount = isMint ? `+${amount}` : `-${amount}`;
          const sig = tx.transaction_signature || "";
          const shortSig = sig ? `${sig.slice(0, 4)}..${sig.slice(-4)}` : "—";
          return {
            type: isMint ? "Deposit" : "Withdraw",
            amount: signedAmount,
            currency,
            dateStr,
            signature: sig,
            shortSig,
          };
        });
        setRows(list);
      } catch (e) {
        console.error(e);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [walletAddress, maxRows]);

  return (
    <div className={`${styles.card} ${styles.tableCard}`}>
      <div className={styles.tableCardHeader}>
        <h3 className={styles.tableCardTitle}>Deposit / Withdraw History</h3>
        <button
          type="button"
          className={styles.viewAllLink}
          onClick={() => navigate("/transaction")}
        >
          View All
        </button>
      </div>
      <table className={styles.dataTable}>
        <thead>
          <tr>
            <th>Type</th>
            <th>Amount</th>
            <th>Date</th>
            <th>Tx</th>
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr>
              <td colSpan={4} className={styles.loadingRow}>
                Loading…
              </td>
            </tr>
          )}
          {!loading && rows.length === 0 && (
            <tr>
              <td colSpan={4} className={styles.loadingRow}>
                No transactions
              </td>
            </tr>
          )}
          {!loading &&
            rows.map((row, i) => {
              const isWithdraw = String(row.amount).trim().startsWith("-");
              const typeClass = isWithdraw ? styles.typeWithdraw : styles.typeDeposit;
              const amountClass = isWithdraw ? styles.amountNegative : styles.amountPositive;
              return (
                <tr key={`${row.dateStr}-${i}`}>
                  <td className={typeClass}>{row.type}</td>
                  <td className={amountClass}>
                    {row.amount} {row.currency}
                  </td>
                  <td>{row.dateStr}</td>
                  <td>
                    {row.signature ? (
                      <a
                        href={`https://solscan.io/tx/${row.signature}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.txLink}
                      >
                        {row.shortSig}
                      </a>
                    ) : (
                      row.shortSig
                    )}
                  </td>
                </tr>
              );
            })}
        </tbody>
      </table>
    </div>
  );
}

export default DepositWithdrawCard;
